import { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { api } from "./api";
import { mainMedicalFields } from "./formConfig";
import FooterLogo from "./FooterLogo";

export default function VerifyPatient() {
  const { requestNumber } = useParams();
  const navigate = useNavigate();
  const [patient, setPatient] = useState(null);
  const location = useLocation();

  useEffect(() => {
    api
      .get(`/verify/patient/${requestNumber}`)
      .then((res) => {
        setPatient(res.data);
      })
      .catch((err) => {
        console.error(err);
        navigate("/verify");
      });
  }, [requestNumber, navigate]);

  const goToSignature = () => {
    navigate("/verify/signature", {
      state: {
        patient,
        requestNumber,
        verifyId: location.state?.verifyId,
      },
    });
  };

  const handleUndo = async () => {

    try {

        await api.post(
            `/verify/cancel/${location.state?.verifyId}`
        );

        navigate("/verify");

    } catch (err) {

        console.error(err);

        alert("Unable to cancel verification.");

    }

};

  const medicalSchemeField = mainMedicalFields.find(
    (f) => f.name === "main_medical_scheme"
  );

  if (!patient)
    return <div className="text-center mt-20 text-gray-600">Loading...</div>;

  const sectionFields = {
    "Main Member Information": [
      "main_identification_number",
      "main_surname",
      "main_full_names",
      "main_initials",
      "main_title",
      "main_male",
      "main_female",
      "main_date_of_birth",
      "main_employer",
      "main_cell_number",
      "main_home_number",
      "main_work_number",
      "main_fax_number",
      "main_email_address",
      "main_email_statements",
      "main_postal_address_1",
      "main_postal_address_2",
      "main_postal_address_3",
      "main_postal_address_code",
      "main_physical_address_1",
      "main_physical_address_2",
      "main_physical_address_3",
      "main_physical_address_code",
    ],
    "Medical Aid Information": [
      "main_medical_scheme",
      "main_medical_scheme_plan",
      "main_medical_member_number",
      "main_medical_member_main_dep_code",
      "main_medical_gap_cover",
    ],
    "Patient Information": [
      "patient_identification_number",
      "patient_surname",
      "patient_full_names",
      "patient_nick_names",
      "patient_initials",
      "patient_title",
      "patient_male",
      "patient_female",
      "patient_date_of_birth",
      "patient_cell_number",
      "patient_use_appoint_test_results",
      "patient_home_number",
      "patient_work_number",
      "patient_email_address",
      "patient_occupation",
      "patient_marital_status",
      "patient_relationship_main",
      "patient_dep_code",
      "patient_age",
      "patient_height",
      "patient_weight",
      "patient_referring_doctor",
      "patient_referring_doctor_nuiber",
      "patient_gp",
      "patient_gp_number",
    ],
    "Next of Kin Information": [
      "next_kin_initials",
      "next_kin_title",
      "next_kin_surname",
      "next_kin_full_names",
      "next_kin_cell_number",
      "next_kin_relationship",
    ],
  };

  const formatLabel = (key) => {
    return key
      .replace(/^(main_|patient_|next_kin_)/, "") // Remove leading context
      .replace(/_/g, " ") // Convert underscores to spaces
      .replace(/\b\w/g, (l) => l.toUpperCase()); // Capitalize each word
  };

  const calculateAge = (dob) => {
    if (!dob) return "";

    const birthDate = new Date(dob);
    const today = new Date();

    let age = today.getFullYear() - birthDate.getFullYear();

    const month = today.getMonth() - birthDate.getMonth();

    if (month < 0 || (month === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return age;
  };

  return (
    <div
      className="min-h-screen w-full bg-cover bg-center text-gray-800"
      style={{
        backgroundImage: `radial-gradient(circle at top left, #A8A8A8, #8C8C8C, #85D3DE, #8DDFEB)`,
      }}
    >
      <div className="flex justify-center pt-6">
        <img
          src="/InfoMedi_logo.png"
          alt="InfoMedi Logo"
          className="h-20 drop-shadow"
        />
      </div>

      <div className="bg-white/30 backdrop-blur-md shadow-xl rounded-xl px-6 py-8 mt-8 max-w-3xl mx-auto border border-white/40">
      <div className="mb-4">
        <button
          onClick={handleUndo}
          className="text-gray-700 hover:text-red-600 font-semibold"
        >
          ← Undo Verification
        </button>
      </div>
        <div className="text-center mb-10">
          <h1 className="text-4xl font-bold text-blue-800">
            Patient Verification
          </h1>

          <p className="text-gray-700 mt-3">
            Please verify your information below before signing.
          </p>
        </div>

        {Object.entries(sectionFields).map(([sectionTitle, keys]) => {
          const fields = keys;
          return (
            <div key={sectionTitle} className="mb-6">
              <h2 className="text-xl font-bold text-blue-700 border-b border-blue-200 mb-4 pb-1">
                {sectionTitle}
              </h2>
              <div className="grid-cols-1 md:grid-cols-2">
                <div className="flex flex-col gap-0">
                  {fields.map((key) =>
                    key === "signature_image" && patient[key] ? (
                      <div key={key}>
                        <strong className="text-blue-700">
                          {formatLabel(key)}:
                        </strong>
                        <br />
                        <img
                          src={`data:image/png;base64,${patient[key]}`}
                          alt="Signature"
                          className="border rounded mt-2 max-w-full"
                        />
                      </div>
                    ) : (
                      <div key={key} className="mb-1">
                        <div className="text-sm font-semibold text-blue-700">
                          {formatLabel(key)}
                        </div>

                        {[
                          "main_male",
                          "main_female",
                          "patient_male",
                          "patient_female",
                          "main_email_statements",
                          "main_medical_gap_cover",
                        ].includes(key) ? (
                          key === "main_male" ? (
                            <div className="flex gap-6 mt-2">
                              <label className="flex items-center gap-2">
                                <input
                                  type="radio"
                                  name="main_gender"
                                  checked={patient.main_male}
                                  onChange={() =>
                                    setPatient((prev) => ({
                                      ...prev,
                                      main_male: true,
                                      main_female: false,
                                    }))
                                  }
                                  className="scale-150 cursor-pointer"
                                />
                                Male
                              </label>

                              <label className="flex items-center gap-2">
                                <input
                                  type="radio"
                                  name="main_gender"
                                  checked={patient.main_female}
                                  onChange={() =>
                                    setPatient((prev) => ({
                                      ...prev,
                                      main_male: false,
                                      main_female: true,
                                    }))
                                  }
                                  className="scale-150 cursor-pointer"
                                />
                                Female
                              </label>
                            </div>
                          ) : key === "main_female" ? null : key ===
                            "patient_male" ? (
                            <div className="flex gap-6 mt-2">
                              <label className="flex items-center gap-2">
                                <input
                                  type="radio"
                                  name="patient_gender"
                                  checked={patient.patient_male}
                                  onChange={() =>
                                    setPatient((prev) => ({
                                      ...prev,
                                      patient_male: true,
                                      patient_female: false,
                                    }))
                                  }
                                  className="scale-150 cursor-pointer"
                                />
                                Male
                              </label>

                              <label className="flex items-center gap-2">
                                <input
                                  type="radio"
                                  name="patient_gender"
                                  checked={patient.patient_female}
                                  onChange={() =>
                                    setPatient((prev) => ({
                                      ...prev,
                                      patient_male: false,
                                      patient_female: true,
                                    }))
                                  }
                                  className="scale-150 cursor-pointer"
                                />
                                Female
                              </label>
                            </div>
                          ) : key === "patient_female" ? null : (
                            <input
                              type="checkbox"
                              checked={!!patient[key]}
                              onChange={(e) =>
                                setPatient((prev) => ({
                                  ...prev,
                                  [key]: e.target.checked,
                                }))
                              }
                              className="mt-2 h-5 w-5"
                            />
                          )
                        ) : key.includes("date_of_birth") ? (
                          <input
                            type="date"
                            value={patient[key]?.slice(0, 10) || ""}
                            onChange={(e) => {
                              const dob = e.target.value;

                              setPatient((prev) => ({
                                ...prev,

                                [key]: dob,

                                ...(key === "patient_date_of_birth" && {
                                  patient_age: calculateAge(dob),
                                }),
                              }));
                            }}
                            className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none"
                          />
                        ) : key === "main_medical_scheme" ? (
                          <>
                            <input
                              list="medicalSchemes"
                              value={patient[key] ?? ""}
                              onChange={(e) =>
                                setPatient((prev) => ({
                                  ...prev,
                                  [key]: e.target.value,
                                }))
                              }
                              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none"
                            />

                            <datalist id="medicalSchemes">
                              {medicalSchemeField.options.map((option) => (
                                <option key={option} value={option} />
                              ))}
                            </datalist>
                          </>
                        ) : key === "patient_age" ? (
                        <input
                          type="text"
                          value={patient[key] ?? ""}
                          readOnly
                          className="w-full p-2 bg-gray-100 border border-gray-300 rounded-md cursor-not-allowed"
                        />
                      ) : (
                        <input
                          type="text"
                          value={patient[key] ?? ""}
                          onChange={(e) =>
                            setPatient((prev) => ({
                              ...prev,
                              [key]: e.target.value,
                            }))
                          }
                          className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none"
                        />
                      )}
                      </div>
                    )
                  )}
                </div>
              </div>
            </div>
          );
        })}

        <div className="mt-10 text-center">
          <button
            onClick={goToSignature}
            className="bg-blue-700 hover:bg-blue-800 text-white px-12 py-4 rounded-xl text-xl shadow-lg"
          >
            Continue
          </button>
        </div>
      </div>
      <FooterLogo />
    </div>
  );
}
